import { CameraIcon, CheckCircledIcon } from '@radix-ui/react-icons';
import {
  Box,
  Button,
  Card,
  DataList,
  Flex,
  Grid,
  Heading,
  Text,
} from '@radix-ui/themes';
import { NotifyHelper } from 'classes/helpers/notify.helper';
import { CommonCallout } from 'components/common/callouts';
import { ErrorBoundary } from 'components/common/error-boundary';
import { ALLOW_RETAKING_CAL_IMAGES } from 'components/machine/dialogs/vision-calibration/camera';
import { VISION_CAL_COLORS } from 'enums/charts.enums';
import { toPng } from 'html-to-image';
import { IMenuAction } from 'interfaces/i-menus';
import { RADIX } from 'lib_ts/enums/radix-ui';
import {
  CALIBRATION_FOLLOWER_ONLY_FRAME_INDICES,
  CALIBRATION_LEADER_ONLY_FRAME_INDICES,
  VisionCalibrationCameraType,
} from 'lib_ts/enums/vision.enums';
import {
  ICameraError,
  IOutOfSpecFrame,
  IVisionCameraResult,
} from 'lib_ts/interfaces/vision/i-vision-camera-calibration';
import { first } from 'lodash';
import { useMemo, useState } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ResponsiveContainer,
  Tooltip,
  TooltipProps,
  XAxis,
  YAxis,
} from 'recharts';
import {
  NameType,
  ValueType,
} from 'recharts/types/component/DefaultTooltipContent';

interface IHistogramData {
  index: number;
  error: number;
}

const CustomTooltip = ({
  active,
  payload,
}: TooltipProps<ValueType, NameType>) => {
  if (!active) {
    return null;
  }

  if (!payload) {
    return null;
  }

  if (payload.length === 0) {
    return null;
  }

  const data: IHistogramData = first(payload)?.payload;

  if (!data) {
    return null;
  }

  return (
    <div className="custom-tooltip">
      Image Index: {data.index}
      <br />
      Error: {data.error.toFixed(2)}
    </div>
  );
};

const sanitizeGoogleFonts = (): HTMLLinkElement[] => {
  // remove google fonts to prevent CORS issues when exporting charts as images
  const fonts = Array.from(
    document.querySelectorAll<HTMLLinkElement>(
      'link[rel="stylesheet"][href*="fonts.googleapis.com"]'
    )
  );

  fonts.forEach((f) => f.remove());

  // so we can add back afterwards
  return fonts;
};

const restoreGoogleFonts = (fonts: HTMLLinkElement[]) => {
  fonts.forEach((f) => document.head.appendChild(f));
};

const getChartPNG = async (id: string): Promise<string> => {
  const wrapper = document.querySelector<HTMLElement>(id);

  if (!wrapper) {
    return '';
  }

  const pngDataUrl = await toPng(wrapper, {
    pixelRatio: 2,
    backgroundColor: 'rgba(29, 29, 33, 0.7)',
  });

  return pngDataUrl;
};

const getChartPNGs = async () => {
  const fonts = sanitizeGoogleFonts();

  try {
    const [leaderPNG, followerPNG, stereoPNG] = await Promise.all([
      getChartPNG('#Leader-stereo-cal-chart'),
      getChartPNG('#Follower-stereo-cal-chart'),
      getChartPNG('#Stereo-stereo-cal-chart'),
    ]);

    return [
      {
        cameraType: VisionCalibrationCameraType.leader,
        imageBase64: leaderPNG,
      },
      {
        cameraType: VisionCalibrationCameraType.follower,
        imageBase64: followerPNG,
      },
      {
        cameraType: VisionCalibrationCameraType.stereo,
        imageBase64: stereoPNG,
      },
    ];
  } finally {
    restoreGoogleFonts(fonts);
  }
};

export const VisionCalibrationChart = (props: {
  header: string;
  error: ICameraError;
  onRetakeImages?: () => void;
}) => {
  const data = useMemo(() => {
    return props.error.reprojection.map((v, i) => {
      const o: IHistogramData = {
        index: i,
        error: v,
      };

      return o;
    });
  }, [props.error]);

  const imgIndicesOver = useMemo(() => {
    const output: number[] = [];

    props.error.reprojection.forEach((v, i) => {
      if (v <= props.error.cutoff) {
        return;
      }

      output.push(i);
    });

    return output;
  }, [props.error.reprojection, props.error.cutoff]);

  const maxYAxis = useMemo(
    () => Math.ceil(100 * Math.max(...props.error.reprojection)) / 100,
    [props.error.reprojection]
  );

  const imgIndicesOverText = imgIndicesOver.map((v) => `Img ${v}`).join(', ');

  return (
    <ErrorBoundary identifier="VCCResultTab">
      <Card style={{ width: '100%' }} id={`${props.header}-stereo-cal-chart`}>
        <Heading size="3" mb="4">
          {props.header}
        </Heading>

        <Grid columns="6" gap="4" style={{ width: '100%' }}>
          <Box gridColumn="span 5">
            <ResponsiveContainer width="100%" height={200}>
              <BarChart
                data={data}
                margin={{
                  top: 20,
                  bottom: 20,
                }}
              >
                <CartesianGrid strokeDasharray="3 3" />

                <XAxis
                  name="Image Index"
                  type="category"
                  dataKey="index"
                  interval={4}
                />

                <YAxis
                  name="Reprojection Error"
                  type="number"
                  dataKey="error"
                  orientation="left"
                  domain={[0, maxYAxis]}
                  interval="preserveStartEnd"
                />

                <Tooltip content={<CustomTooltip />} />

                <Bar name="Reprojection Error" dataKey="error">
                  {data.map((entry) => {
                    const needRetaking = entry.error > props.error.cutoff;
                    return (
                      <Cell
                        key={`cell-${entry.index}`}
                        radius={4}
                        fill={
                          needRetaking
                            ? VISION_CAL_COLORS[1]
                            : VISION_CAL_COLORS[0]
                        }
                      />
                    );
                  })}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </Box>

          <DataList.Root size="1" orientation="vertical">
            <DataList.Item>
              <DataList.Label>
                <Text truncate>Cutoff</Text>
              </DataList.Label>
              <DataList.Value>{props.error.cutoff}</DataList.Value>
            </DataList.Item>
            <DataList.Item>
              <DataList.Label>
                <Text truncate>Rejected Images</Text>
              </DataList.Label>
              <DataList.Value>
                <Text truncate>{imgIndicesOver.length}</Text>
              </DataList.Value>
            </DataList.Item>
            <DataList.Item>
              <DataList.Label>
                <Text truncate>Images &gt; {props.error.cutoff}</Text>
              </DataList.Label>
              <DataList.Value>
                <Text title={imgIndicesOverText || undefined} truncate>
                  {imgIndicesOverText || 'N/A'}
                </Text>
              </DataList.Value>
            </DataList.Item>
            {ALLOW_RETAKING_CAL_IMAGES && props.onRetakeImages && (
              <Button
                size="1"
                color={RADIX.COLOR.ACCENT}
                variant="solid"
                className="btn-block'"
                onClick={props.onRetakeImages}
              >
                {`Retake ${props.header}`}
              </Button>
            )}
          </DataList.Root>
        </Grid>
      </Card>
    </ErrorBoundary>
  );
};

// * NOTE: We only allow retaking the highest-error Leader-specific or Follower-specific frame since retaking a Stereo frame can impact results for all three types
export const VisionCalibrationCameraResultTab = (props: {
  result: IVisionCameraResult;
  outOfSpecFrames: IOutOfSpecFrame[];
  onRetakeImages: (startingIndex: number) => void;
  onComplete: () => void;
  onReportResults: (
    charts: { cameraType: VisionCalibrationCameraType; imageBase64: string }[]
  ) => void;
}) => {
  const {
    result,
    outOfSpecFrames,
    onComplete,
    onRetakeImages,
    onReportResults,
  } = props;

  // loading state for uploading results to slack
  const [reportingResults, setReportingResults] = useState(false);
  const numImagesToRetake = outOfSpecFrames.length;

  // 10th leader result should refer to the 10th calibration frame
  const outOfSpecLeaderFrames = useMemo(
    () =>
      outOfSpecFrames
        .filter((f) => f.type === VisionCalibrationCameraType.leader)
        .filter(({ index }) =>
          CALIBRATION_LEADER_ONLY_FRAME_INDICES.includes(index)
        ) // does not include stereo frames
        .sort((a, b) => b.value - a.value),
    [outOfSpecFrames]
  );

  // 10th follower result should refer to the 26th calibration frame
  const outOfSpecFollowerFrames = useMemo(
    () =>
      outOfSpecFrames
        .filter((f) => f.type === VisionCalibrationCameraType.follower)
        .filter(({ index }) =>
          CALIBRATION_FOLLOWER_ONLY_FRAME_INDICES.includes(index)
        ) // does not include stereo frames
        .sort((a, b) => b.value - a.value),
    [outOfSpecFrames]
  );

  const showCompleteCallout =
    !ALLOW_RETAKING_CAL_IMAGES ||
    (ALLOW_RETAKING_CAL_IMAGES && numImagesToRetake === 0);

  const actions: IMenuAction[] = [
    {
      label: 'Report Results',
      size: '2',
      color: RADIX.COLOR.SECONDARY,
      disabled: reportingResults,
      onClick: async () => {
        try {
          setReportingResults(true);

          const charts = await getChartPNGs();
          await onReportResults(charts);

          NotifyHelper.success({
            message_md: 'Successfully reported results',
          });
        } catch (error) {
          console.error('Failed to report VCC results to Slack', error);
          NotifyHelper.error({
            message_md: 'Failed to report results',
          });
          return;
        } finally {
          setReportingResults(false);
        }
      },
    },
    {
      label: 'Complete Calibration',
      size: '2',
      color: RADIX.COLOR.SUCCESS,
      disabled: reportingResults,
      onClick: onComplete,
    },
  ];

  return (
    <ErrorBoundary identifier="VCCResultTab">
      <Flex direction="column" gap="4">
        {ALLOW_RETAKING_CAL_IMAGES && numImagesToRetake > 0 && (
          <CommonCallout
            icon={<CameraIcon />}
            text={`${numImagesToRetake} images need retaking to complete calibration`}
            color={RADIX.COLOR.WARNING}
            actionsDirection="row"
            actions={actions}
          />
        )}
        {showCompleteCallout && (
          <CommonCallout
            icon={<CheckCircledIcon />}
            text="All images have been captured successfully"
            color={RADIX.COLOR.SUCCESS}
            actionsDirection="row"
            actions={actions}
          />
        )}
        <VisionCalibrationChart
          header="Leader"
          error={result.leader}
          onRetakeImages={() => {
            const highestErrorLeaderFrame = outOfSpecLeaderFrames[0];

            if (!highestErrorLeaderFrame) {
              return;
            }

            onRetakeImages(highestErrorLeaderFrame.index);
          }}
        />
        <VisionCalibrationChart
          header="Follower"
          error={result.follower}
          onRetakeImages={() => {
            const highestErrorFollowerFrame = outOfSpecFollowerFrames[0];

            if (!highestErrorFollowerFrame) {
              return;
            }

            onRetakeImages(highestErrorFollowerFrame.index);
          }}
        />
        <VisionCalibrationChart header="Stereo" error={result.stereo} />
      </Flex>
    </ErrorBoundary>
  );
};
